/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { Account } from '@carbon/react/icons';
import {
  Button,
  CodeSnippet,
  CodeSnippetSkeleton,
  FormLabel,
  ListItem,
  TextInput,
  OrderedList
} from 'carbon-components-react';
import _ from 'lodash';
import React, { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { shallowEqual } from 'react-redux';
import { useMount } from 'react-use';

import { useIsNewIA } from '@ibm-watson/shared/modules/ia/hooks/useIsNewIA';
import { copyContentToClipboard } from '@ibm-watson/shared/modules/ia/util/clipboard/clipboard';
import { useAppSelector } from '@ibm-watson/shared/store';
import { FormattedMessageWithLink } from '../../../helpers/linkHelpers';
import { useChannelHelpers } from '../../../hooks/useChannelHelpers';
import { useFormHelpers } from '../../../hooks/useFormHelpers';
import {
  isSubscribeIntegrationLoading,
  selectDraftIntegration
} from '../../../store/channels/selectors';
import { ChannelContentProps } from '../../../util/types';
import { TabContent } from '../../common/tabbed-header/TabContent';
import { sharedMessages } from '../../messages';
import { TwilioAuth } from '../../twilio-auth/TwilioAuth';
import { links } from '../links';
import { FIELDS } from '../schema';
import { TwilioMessagingQuickSetup } from '../twilio-new/messages';
import { BasicSetup as messages } from './messages';

export const BasicSetup = ({
  assistantId,
  environmentId,
  readOnly
}: ChannelContentProps) => {
  const { formatMessage: t } = useIntl();

  const formMethods = useFormContext();
  const {
    getFieldValue,
    isInvalidOrEmpty,
    registerInputField,
    updateFieldValue
  } = useFormHelpers(formMethods);
  const { subscribeForWebhookUrl } = useChannelHelpers(formMethods);

  const phoneNumberChangeState = useState<boolean>(false);

  const draftIntegration = useAppSelector(selectDraftIntegration, shallowEqual);
  const isSubscribeLoading = useAppSelector(isSubscribeIntegrationLoading);

  /**
   * If the webhook URL is empty at mount (likely because of pre-v2
   * partial integrations), then we mark all the secret fields dirty
   * so Tanya will have to resupply these fields and re-subscribe
   * the integration.
   */
  useMount(() => {
    if (isInvalidOrEmpty(FIELDS.WEBHOOK_URL) && !_.isNil(assistantId)) {
      updateFieldValue(FIELDS.TWILIO_ACCOUNT_SID, '');
      updateFieldValue(FIELDS.TWILIO_AUTHENTICATION_TOKEN, '');
    }
  });

  /**
   * Button method to re-subscribe the integration.
   */
  const resubscribeIntegration = useCallback(async () => {
    subscribeForWebhookUrl(
      assistantId,
      environmentId,
      draftIntegration,
      {
        account_sid: getFieldValue(FIELDS.TWILIO_ACCOUNT_SID),
        auth_token: getFieldValue(FIELDS.TWILIO_AUTHENTICATION_TOKEN)
      },
      FIELDS.WEBHOOK_URL
    );
  }, [
    assistantId,
    draftIntegration,
    environmentId,
    getFieldValue,
    subscribeForWebhookUrl
  ]);

  return (
    <TabContent
      ImageComponent={() => <Account size={32} />}
      title={<FormattedMessage {...messages.title} />}>
      <div className="TabContent__body-content">
        <div className="TabContent__subtitle TabContent__link-larger-text">
          <FormattedMessageWithLink
            url={
              useIsNewIA()
                ? links.learnMoreDeploySmsNewIA
                : links.learnMoreDeploySmsLink
            }
            {...messages.subtitle}
          />
        </div>
        <div className="TabContent__instructions-no-spacer">
          <h4>
            <FormattedMessage {...messages.twilioAccountInformation} />
          </h4>
          <div className="TabContent__spacer16" />
          <TwilioAuth
            accountSidField={FIELDS.TWILIO_ACCOUNT_SID}
            authTokenField={FIELDS.TWILIO_AUTHENTICATION_TOKEN}
          />
        </div>
        <h4>
          <FormattedMessage {...messages.setup} />
        </h4>
        <div className="TabContent__spacer8" />
        <FormLabel>
          <FormattedMessage {...messages.instructionsDescription} />
        </FormLabel>
        <div className="TabContent__spacer8" />
        <OrderedList className="TabContent__subtitle">
          <ListItem>
            <FormattedMessage {...messages.instructionsItemOne} />
            <div className="TabContent__spacer8" />
            <TextInput
              {...registerInputField({
                field: FIELDS.FROM_PHONE_NUMBER,
                labelText: TwilioMessagingQuickSetup.phoneNumber,
                placeholderText: messages.phonePlaceholder,
                changeState: phoneNumberChangeState,
                readOnly
              })}
            />
          </ListItem>
          <ListItem>
            <FormattedMessage {...messages.instructionsItemTwo} />
          </ListItem>
          <ListItem>
            <FormattedMessage {...messages.instructionsItemThree} />
            <div className="TabContent__spacer8" />
            <FormLabel className="TabContent__field-label">
              <FormattedMessage {...messages.webhookUri} />
            </FormLabel>
            <div className="TabContent__spacer8" />
            {!_.isNil(getFieldValue(FIELDS.WEBHOOK_URL)) ? (
              <CodeSnippet
                copyButtonDescription={t(sharedMessages.copyToClipboard)}
                copyLabel={t(sharedMessages.copyToClipboard)}
                feedback={t(sharedMessages.copied)}
                onClick={() => {
                  copyContentToClipboard(getFieldValue(FIELDS.WEBHOOK_URL));
                }}
                type="single">
                {getFieldValue(FIELDS.WEBHOOK_URL)}
              </CodeSnippet>
            ) : isSubscribeLoading ? (
              <CodeSnippetSkeleton />
            ) : (
              <Button
                id="sms-resubscribe-button"
                kind="primary"
                disabled={
                  isInvalidOrEmpty(FIELDS.TWILIO_ACCOUNT_SID) ||
                  isInvalidOrEmpty(FIELDS.TWILIO_AUTHENTICATION_TOKEN)
                }
                onClick={resubscribeIntegration}>
                <FormattedMessage {...messages.retrieveWebhookUri} />
              </Button>
            )}
          </ListItem>
        </OrderedList>
      </div>
    </TabContent>
  );
};
